import styled from '@emotion/styled';
import React from 'react';
import ColorField from './color-field';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import MixedSelectField from './mixed-select-field';
import NumberField from './number-field';
import type { VignetteControl, VignetteDirection } from './use-vignette';

const MIXED = '(разное)';

const DIRECTION_OPTIONS: { value: VignetteDirection; label: string }[] = [
  { value: 'right', label: 'Вправо →' },
  { value: 'left', label: 'Влево ←' },
  { value: 'up', label: 'Вверх ↑' },
  { value: 'down', label: 'Вниз ↓' },
];

interface VignetteSectionProps {
  control: VignetteControl;
}

const toPercent = (fraction: number | undefined): number | undefined =>
  fraction === undefined ? undefined : Math.round(fraction * 100);

/**
 * Shared "Vignette" inspector section: a fade-out fill that stays a solid base
 * color and dissolves to transparent toward one side, starting at an adjustable
 * point (default the middle). Toggle on to reveal the base color, the fade
 * direction, and the start point. The visible gradient is materialized from
 * `data.vignette` by `editor.vignette` — this section only drives the config
 * through `useVignette`.
 */
const VignetteSection: React.FC<VignetteSectionProps> = ({ control }) => {
  const on = control.enabled.value === true;

  return (
    <InspectorSection
      title="Виньетка"
      headerAction={
        <ToggleLabel>
          <input type="checkbox" checked={on} onChange={(e) => control.setEnabled(e.target.checked)} />
          Включить
        </ToggleLabel>
      }
    >
      {on && (
        <>
          <InspectorRow label="Базовый цвет">
            <ColorField
              value={control.color.isMixed ? undefined : control.color.value}
              onChange={control.setColor}
              placeholder={control.color.isMixed ? MIXED : undefined}
            />
          </InspectorRow>
          <InspectorRow label="Направление">
            <MixedSelectField
              value={control.direction.value}
              isMixed={control.direction.isMixed}
              onChange={(v) => control.setDirection(v as VignetteDirection)}
              options={DIRECTION_OPTIONS}
            />
          </InspectorRow>
          <InspectorRow label="Затухание с">
            <NumberField
              value={control.fadeStart.isMixed ? undefined : toPercent(control.fadeStart.value)}
              onChange={(v) => control.setFadeStart(Math.min(Math.max(v, 0), 100) / 100)}
              min={0}
              max={100}
              step={5}
              emptyValue={50}
              placeholder={control.fadeStart.isMixed ? MIXED : undefined}
            />
          </InspectorRow>
        </>
      )}
    </InspectorSection>
  );
};

export default VignetteSection;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6366f1;
  cursor: pointer;
  user-select: none;

  input {
    cursor: pointer;
  }
`;
